import {
	afterNextRender,
	Component,
	computed,
	forwardRef,
	input,
	inject,
	signal,
	viewChild,
	Injector,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { InstitutionBranch } from '../../../../api/app-types/institution.app';
import { MatCheckbox } from '@angular/material/checkbox';
import { LvrField } from '../../../../core/components/field/field';
import { MatAutocompleteModule, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatFormField } from '@angular/material/form-field';
import { TranslocoPipe } from '@jsverse/transloco';
import { MatIcon } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';

@Component({
	selector: 'lvr-responsibility',
	imports: [
		MatCheckbox,
		LvrField,
		MatAutocompleteModule,
		MatFormField,
		MatInputModule,
		TranslocoPipe,
		MatIcon,
		MatButtonModule,
	],
	templateUrl: './responsibility.html',
	styleUrl: './responsibility.scss',
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => LvrResponsibility),
			multi: true,
		},
	],
})
export class LvrResponsibility implements ControlValueAccessor {
	private readonly autocompleteTrigger = viewChild(MatAutocompleteTrigger);
	private readonly injector = inject(Injector);

	public readonly branches = input.required<InstitutionBranch[] | undefined>();
	public readonly label = input.required<string>();

	private readonly value = signal<number[]>([]);
	public readonly isDisabled = signal<boolean>(false);
	public readonly focused = signal(false);
	public readonly filterText = signal('');

	public readonly filteredBranches = computed(() => {
		const term = this.filterText().trim().toLocaleLowerCase();
		if (!term) return this.branches() ?? [];
		return (this.branches() ?? []).filter((branch) => branch.name.toLocaleLowerCase().includes(term));
	});

	public readonly selectedBranches = computed(() => {
		const selected = new Set(this.value());
		return this.branches()?.filter((branch) => selected.has(branch.id)) ?? [];
	});

	public readonly displayText = computed(() => {
		if (this.focused()) return this.filterText();
		return this.selectedBranches()
			.map((branch) => branch.name)
			.join(', ');
	});

	public onFocus(): void {
		this.focused.set(true);
	}

	public onBlur(): void {
		this.focused.set(false);
		this.filterText.set('');
		this.onTouched();
	}

	public onSearch(event: Event): void {
		this.filterText.set((event.target as HTMLInputElement).value);
	}

	public clearSearch(): void {
		this.filterText.set('');
	}

	// keeps Material's own trigger-value write in sync so selection never overwrites the search text
	public readonly displayWithBranch = (): string => this.filterText();

	public onOptionSelected(branch: InstitutionBranch, input: HTMLInputElement): void {
		const searchText = this.filterText();
		this.toggle(branch.id);
		this.filterText.set(searchText);
		input.value = searchText;
		afterNextRender(() => this.autocompleteTrigger()?.openPanel(), { injector: this.injector });
	}

	// eslint-disable-next-line @typescript-eslint/no-empty-function
	private onChange = (_: number[] | null) => {};
	// eslint-disable-next-line @typescript-eslint/no-empty-function
	private onTouched = () => {};

	public writeValue(value: number[] | null): void {
		this.value.set(value ?? []);
	}

	public registerOnChange(fn: (value: number[] | null) => void): void {
		this.onChange = fn;
	}

	public registerOnTouched(fn: () => void): void {
		this.onTouched = fn;
	}

	public setDisabledState(isDisabled: boolean): void {
		this.isDisabled.set(isDisabled);
	}

	public toggle(branchId: number) {
		const current = this.value();

		if (current.includes(branchId)) {
			const update = current.filter((id) => id !== branchId);
			this.value.set(update);
			this.onChange(update);
		} else {
			const update = [...current, branchId];
			this.value.set(update);
			this.onChange(update);
		}
	}

	public isBranchSelected(branchId: number) {
		return this.value().includes(branchId);
	}

	public trackById(_: number, branch: InstitutionBranch) {
		return branch.id;
	}
}
